import { Component, OnInit } from '@angular/core';
import { ApiRequestService } from '../api-request.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-system-banners-display',
  templateUrl: './system-banners-display.component.html',
  styleUrls: ['./system-banners-display.component.scss']
})
export class SystemBannersDisplayComponent implements OnInit {

  /**
   * Currently active banners for the web portal user.
   */
  banners: any[] = [];

  /**
   * Collapsed state for the banner stack. When true, the full banner list is
   * replaced by a single compact summary row. Toggled by the show/hide button
   * rendered below the banners.
   */
  isCollapsed: boolean = false;

  /**
   * Persistence key for the collapsed state so the preference survives a
   * page reload. Stored per user in localStorage (non-critical UX state).
   */
  private readonly collapseStorageKey: string = 'sc_system_banners_collapsed';

  constructor(
    private api: ApiRequestService,
    private utils: UtilsService
  ) {}

  ngOnInit(): void {
    this.restoreCollapsedState();
    this.loadBanners();
  }

  /**
   * Fetch the active system banners for the web portal.
   * Failures are logged but do not block the dashboard.
   */
  private loadBanners(): void {
    this.api.makeRequest('get', 'v2/system-banners', {}, { platform: 'web' })
      .then((response: any) => {
        this.banners = response && Array.isArray(response.data) ? response.data : [];
      })
      .catch((errorResponse) => {
        // Don't break the dashboard if banners fail to load.
        console.warn('Failed to load system banners.', errorResponse);
      });
  }

  /**
   * Get the Material icon for a banner's style.
   *
   * Note on outlined variants: the legacy "Material Icons Outlined" font
   * does not include outlined glyphs for `warning` and `error` (they fall
   * back to filled). To get a consistent outlined look across all four
   * styles, we use `warning_amber` and `error_outline` which are outlined
   * by default in the base Material Icons font.
   */
  getIconForStyle(style: string): string {
    switch (style) {
      case 'warning':
        return 'warning_amber';
      case 'error':
        return 'error_outline';
      case 'success':
        return 'check_circle';
      case 'info':
      default:
        return 'info';
    }
  }

  /**
   * Dismiss a banner. Optimistically removes it from the list on success.
   */
  onDismiss(banner: any): void {
    if ( !banner || !banner.id ) {
      return;
    }

    this.api.makeRequest('post', `v2/system-banners/${banner.id}/dismiss`, {})
      .then(() => {
        this.banners = this.banners.filter((b) => b.id !== banner.id);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Toggle the collapsed state of the banner stack and persist the preference.
   */
  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
    try {
      localStorage.setItem(this.collapseStorageKey, this.isCollapsed ? '1' : '0');
    } catch (_e) {
      // Ignore storage errors (e.g., private browsing).
    }
  }

  /**
   * Restore the collapsed state from localStorage so the user's preference
   * survives a page reload.
   */
  private restoreCollapsedState(): void {
    try {
      const stored: string | null = localStorage.getItem(this.collapseStorageKey);
      this.isCollapsed = stored === '1';
    } catch (_e) {
      this.isCollapsed = false;
    }
  }

  /**
   * Distinct styles present in the current banner stack, preserving the
   * order in which they first appear. Used by the collapsed summary row
   * to render one icon per style rather than one per banner.
   */
  getDistinctStyles(): string[] {
    const seen: { [key: string]: boolean } = {};
    const styles: string[] = [];
    for (const banner of this.banners) {
      const style: string = banner && banner.style ? banner.style : 'info';
      if ( !seen[style] ) {
        seen[style] = true;
        styles.push(style);
      }
    }
    return styles;
  }

  /**
   * trackBy for the *ngFor iteration over banners.
   */
  trackByBannerId(index: number, banner: any): number {
    return banner ? banner.id : index;
  }

  /**
   * trackBy for the distinct styles in the collapsed summary row.
   */
  trackByStyle(index: number, style: string): string {
    return style || String(index);
  }
}
